import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import logoTenacityFooter from '@/assets/logo-tenacity-footer.png';

export const PublicFooter = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };
  return <footer className="relative pt-6 md:pt-12 pb-8 px-4 sm:px-6 lg:px-16 overflow-hidden">
      {/* Gradient background with rounded top */}
      <div className="absolute inset-0 rounded-t-[70px] md:rounded-t-[140px]" style={{
      background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.05) 0%, rgba(0, 0, 0, 0.05) 100%)'
    }} />
      
      <div className="max-w-[1800px] mx-auto relative z-10">
        <div className="flex flex-col lg:flex-row justify-between gap-8 mb-4">
          {/* Left side - Three columns */}
          <div className="grid grid-cols-3 gap-8 md:gap-12 lg:gap-16">
            {/* Members Column */}
            <div>
              <h3 className="text-foreground mb-2 md:mb-4 text-xl md:text-[30px]" style={{
              fontFamily: '"Lexend Deca", sans-serif',
              fontWeight: 400
            }}>
                Members
              </h3>
              <nav className="flex flex-col gap-2.5">
                <Link to="/plans" className="text-sm text-foreground hover:text-foreground/80 transition-colors md:hidden">
                  Join Tenacity
                </Link>
                <Link to="/plans" className="hidden md:block">
                  <button className="flex items-center justify-center gap-[7.727px] font-bold text-xs text-black hover:opacity-90 transition-opacity" style={{
                  width: '170px',
                  height: '30.671px',
                  padding: '7.727px',
                  borderRadius: '7.727px',
                  background: '#FF0'
                }}>
                    JOIN TENACITY
                    <ArrowRight className="w-3 h-3" />
                  </button>
                </Link>
                {!user ? (
                  <Link to="/login" className="text-sm text-foreground hover:text-foreground/80 transition-colors">
                    Log In
                  </Link>
                ) : (
                  <button onClick={handleSignOut} className="text-sm text-foreground hover:text-foreground/80 transition-colors text-left">
                    Log Out
                  </button>
                )}
              </nav>
            </div>

            {/* Explore Column */}
            <div>
              <h3 className="text-foreground mb-2 md:mb-4 text-xl md:text-[30px]" style={{
              fontFamily: '"Lexend Deca", sans-serif',
              fontWeight: 400
            }}>
                Explore
              </h3>
              <nav className="flex flex-col gap-2.5">
                <Link to="/plans" className="text-sm text-foreground hover:text-foreground/80 transition-colors">
                  Plans
                </Link>
                <Link to="/about" className="text-sm text-foreground hover:text-foreground/80 transition-colors">
                  About
                </Link>
                <Link to="/faq" className="text-sm text-foreground hover:text-foreground/80 transition-colors">
                  FAQ
                </Link>
                <Link to="/contact" className="text-sm text-foreground hover:text-foreground/80 transition-colors">
                  Contact
                </Link>
                {user && (
                  <>
                    <Link to="/series" className="text-sm text-foreground hover:text-foreground/80 transition-colors">
                      All Courses
                    </Link>
                    <Link to="/artists" className="text-sm text-foreground hover:text-foreground/80 transition-colors">
                      Artists
                    </Link>
                  </>
                )}
              </nav>
            </div>

            {/* Social Column */}
            <div>
              <h3 className="text-foreground mb-2 md:mb-4 text-xl md:text-[30px]" style={{
              fontFamily: '"Lexend Deca", sans-serif',
              fontWeight: 400
            }}>
                Social
              </h3>
              <nav className="flex flex-col gap-2.5">
                <a href="https://www.instagram.com/tenacityjazz/" target="_blank" rel="noopener noreferrer" className="text-sm text-foreground hover:text-foreground/80 transition-colors flex items-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                  Instagram
                </a>
                <a href="https://x.com/TenacityJazz" target="_blank" rel="noopener noreferrer" className="text-sm text-foreground hover:text-foreground/80 transition-colors flex items-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                  X
                </a>
              </nav>
            </div>
          </div>

          {/* Right side - Logo and Copyright */}
          <div className="flex flex-col items-center gap-8 lg:gap-12 lg:flex lg:items-end lg:justify-start">
            <Link to="/">
              <img 
                src={logoTenacityFooter} 
                alt="Tenacity Jazz Improv Now" 
                className="h-24 lg:h-32 w-auto"
                width={313}
                height={128}
                loading="lazy"
              />
            </Link>
            
            {/* Copyright under logo */}
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-sm text-foreground">
              <span>© Tenacity Music, {new Date().getFullYear()}</span>
              <span className="hidden sm:inline">—</span>
              <Link to="/contact" className="hover:text-foreground/80 transition-colors underline">Help</Link>
              <span className="hidden sm:inline">—</span>
              <Link to="/privacy" className="hover:text-foreground/80 transition-colors underline">Privacy</Link>
              <span className="hidden sm:inline">—</span>
              <Link to="/terms" className="hover:text-foreground/80 transition-colors underline">Terms</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>;
};